import React, { useState } from 'react';
import { Image } from "react-bootstrap";
import { NavigationTab, LogoutTab } from 'Navigation/Components';
import { BiLandscape, BiSearchAlt, BiLogOut, BiSitemap, BiBuildings, BiGitBranch } from 'react-icons/bi';
import { AiOutlineUser } from 'react-icons/ai';
import { FiSettings } from 'react-icons/fi';
import { FaConnectdevelop, FaDatabase, FaEye } from 'react-icons/fa';
import { RiAdminLine } from 'react-icons/ri';
import { BsCloud, BsJournalBookmark } from 'react-icons/bs';
import { MdOutlineSecurity } from 'react-icons/md';
import { FcExpand, FcCollapse } from 'react-icons/fc';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { FaGlobe } from "react-icons/fa";
import SectionHeader from './SectionHeader';
import { isReadOnlyRole, READ_ONLY_TITLE } from 'Auth/permissions';

import './Navigation.css';

const Navigation = (props) => {
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  // Runtime feature flag from the advanced-config settings (replaces the old build-time
  // REACT_APP_PROFILE_ENABLED). Passed down from App, which loads it on boot.
  // Default-on: only an explicit false hides the item.
  const profileEnabled = props.profileEnabled !== false;
  // Test Data Management section — deployment flag c2.tdm.enabled (on by default since 2026-10-22);
  // shown only when the platform reports true, so an API that predates the flag hides it.
  const tdmEnabled = props.tdmEnabled === true;
  // Frame state from App (see useSidebarState): icon rail below 1280px, drawer below 768px.
  const collapsed = props.collapsed === true;
  const tab = { collapsed };
  const readOnly = isReadOnlyRole(props.user && props.user.role);

  return (
    <nav
      id='app-sidebar'
      aria-label='Main'
      className={
        'navigation' + (collapsed ? ' navigation--collapsed' : '') + (props.drawerOpen ? ' navigation--open' : '')
      }
    >
      <div className='d-flex align-items-start'>
        <Image
          alt="C2 Discover Logo"
          className='navigation-c2-logo'
          src="/c2_platform_white.png"
        />
        {props.onToggle && (
          <button
            type='button'
            className='navigation-toggle'
            onClick={props.onToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            data-testid='nav-toggle'
          >
            {collapsed ? <FiChevronsRight aria-hidden='true' /> : <FiChevronsLeft aria-hidden='true' />}
          </button>
        )}
      </div>
        <PerfectScrollbar>
          <div className='d-flex flex-column h-100'>
            <div>
              {/* Landscape for the wider estate. Every data source appears on both
                  landscapes — the per-source landscape split was removed 2026-09-24. */}
              <NavigationTab
                {...tab}
                tooltip='Where the estate risk sits, who owns it, and what changed since the last scan'
                path='/enterprise'
                icon={BiBuildings}
                text='Enterprise Data Landscape'
                testId='nav-enterprise'
              />
              {/* Pinned above the Discover section — the product home page ('/' selects it). */}
              <NavigationTab
                {...tab}
                tooltip='See your risk overview'
                path='/landscape'
                home={true}
                icon={BiLandscape}
                text='Data Privacy Workflow'
                testId='nav-landscape'
              />
              {/* Schema Intelligence is the Profile section's only item, so the whole section
                  (header included) is gated behind PROFILE_ENABLED — an empty header
                  otherwise. */}
              {profileEnabled && (
                <>
                  <SectionHeader title='Fingerprint' />
                  <NavigationTab
                {...tab}
                    tooltip='Cross-source schema identities and lineage'
                    path='/metadata'
                    icon={BiSitemap}
                    text='Schema Intelligence'
                    exactPath={false}
                    testId='nav-schema-intelligence'
                  />
                </>
              )}
              <SectionHeader title='Discover' />
              <NavigationTab
                {...tab}
                tooltip='Run new data Discovery jobs'
                path='/discover'
                icon={BiSearchAlt}
                text='Data Discovery'
                testId='nav-discovery'
              />
              <SectionHeader title='Secure' />
              <NavigationTab
                {...tab}
                tooltip='View your secured data'
                path='/secure'
                icon={MdOutlineSecurity}
                text='Secure Data Landscape'
                exactPath={true}
              />
              <NavigationTab
                {...tab}
                tooltip='Review and edit masking inventories across all discovered data sources'
                path='/inventory-manager'
                icon={BsJournalBookmark}
                text='Inventory Manager'
                exactPath={false}
              />
              <NavigationTab
                {...tab}
                tooltip='Run masking jobs serially as ordered groups, including fan-outs'
                path='/secure/groups'
                icon={BiSitemap}
                text='Orchestration Groups'
                exactPath={true}
              />
              {tdmEnabled && (
                <>
                  <SectionHeader title='Test Data Management' />
                  <NavigationTab
                {...tab}
                    tooltip='Subsets, target groups and staging'
                    path='/tdm'
                    icon={BiGitBranch}
                    text='Test Data Landscape'
                    exactPath={false}
                    testId='nav-tdm'
                  />
                </>
              )}
              <SectionHeader title='Manage' />
              <NavigationTab
                {...tab}
                tooltip='Cloud resources that may hold data needing discovery'
                exactPath={false}
                path='/manage'
                icon={BsCloud}
                text='Cloud Landscape'
              />
            </div>
            <div className='d-flex flex-column flex-grow-1 justify-content-end'>
              <div>
                <button
                  type='button'
                  className='nav-section-header settings-accordion-toggle'
                  onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                  aria-expanded={isSettingsExpanded}
                  aria-controls='settings-sub-items'
                >
                  <span>Settings</span>
                  <span className='settings-accordion-caret'>
                    {isSettingsExpanded ? <FcCollapse /> : <FcExpand />}
                  </span>
                </button>
                {isSettingsExpanded && (
                  <div id='settings-sub-items'>
                    <NavigationTab
                {...tab}
                      tooltip='Manage your data sources'
                      path='/sources'
                      icon={FaConnectdevelop}
                      text='Data sources'
                      testId='nav-data-sources'
                    />
                    <NavigationTab
                {...tab}
                      tooltip='Manage your sampling rules'
                      path='/rules'
                      icon={FiSettings}
                      text='Sampling Rules'
                    />
                    <NavigationTab
                {...tab}
                      tooltip='View and upload JDBC drivers'
                      path='/drivers'
                      icon={FaDatabase}
                      text='Drivers'
                      exactPath={true}
                    />
                    <NavigationTab
                {...tab}
                      tooltip='View and manage your user settings'
                      path='/user'
                      icon={AiOutlineUser}
                      text='My User'
                      exactPath={true}
                    />
                    { props.isAdmin &&
                      <NavigationTab
                {...tab}
                        tooltip='Register and configure 3rd-party service integrations'
                        path='/integrations'
                        icon={FaGlobe}
                        text='Integrations'
                        exactPath={true}
                      />
                    }
                    { props.isAdmin &&
                      <NavigationTab
                {...tab}
                        tooltip='Manage users and user privileges'
                        path='/admin'
                        icon={RiAdminLine}
                        text='Admin Panel'
                      />
                    }
                    { props.isAdmin &&
                      <NavigationTab
                {...tab}
                        tooltip='Fallback credentials for data sources the workload identity cannot reach'
                        path='/cloud-accounts'
                        icon={BsCloud}
                        text='Alternate Credentials'
                      />
                    }
                  </div>
                )}
                {readOnly && (
                  <div
                    className='navigation-readonly-badge'
                    title={READ_ONLY_TITLE}
                    data-testid='nav-readonly-badge'
                  >
                    <FaEye className='mr-2' />
                    {!collapsed && <span>Read-only account</span>}
                  </div>
                )}
                <LogoutTab
                  {...tab}
                  tooltip='Logout of current user'
                  icon={BiLogOut}
                  text='Logout'
                />
              </div>
            </div>
          </div>
        </PerfectScrollbar>
    </nav>
  );
}

export default Navigation;